import type { Abi, Address } from "viem"
import type { Config } from "wagmi"
import { simulateContract } from "@wagmi/core"
import { encodeFunctionData } from "viem"

import {
  thickV2NfpMulticallAbi,
  thickV2NfpPositionCollectAbi,
  thickV2NfpPositionDecreaseLiquidityAbi,
} from "@/domains/abis/thick-v2-nfpm-abi"
import type { TransactionReturnType } from "@/domains/on-chain"
import { simulatedTransaction } from "@/domains/on-chain/simulated-transaction"

interface DecreaseThickNfpPositionProps {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  liquidity: bigint
  amount0Min: bigint
  amount1Min: bigint
  slippagePercent?: number
  deadline: number
  nfpManagerAddress: Address
}

/**
 * Decrease liquidity on a thick-v2 position and collect the withdrawn
 * underlying tokens in a single multicall. The NFPM resolves pool + fee from
 * `tokenId` so the encoder does not need a `ThickV2PoolKey`.
 */
export const decreaseThickNfpPosition = async ({
  account,
  chainId,
  config,
  tokenId,
  liquidity,
  amount0Min,
  amount1Min,
  slippagePercent,
  deadline,
  nfpManagerAddress,
}: DecreaseThickNfpPositionProps): Promise<TransactionReturnType> => {
  try {
    const [resolvedAmount0Min, resolvedAmount1Min] =
      slippagePercent === undefined
        ? [amount0Min, amount1Min]
        : await quoteLiveDecreaseMins({
            account,
            chainId,
            config,
            tokenId,
            liquidity,
            slippagePercent,
            deadline,
            nfpManagerAddress,
          })

    const decreaseData = encodeFunctionData({
      abi: thickV2NfpPositionDecreaseLiquidityAbi as Abi,
      functionName: "decreaseLiquidity",
      args: [
        {
          tokenId,
          liquidity,
          amount0Min: resolvedAmount0Min,
          amount1Min: resolvedAmount1Min,
          deadline: BigInt(deadline),
        },
      ],
    })

    const collectData = encodeFunctionData({
      abi: thickV2NfpPositionCollectAbi as Abi,
      functionName: "collect",
      args: [
        {
          tokenId,
          recipient: account,
          amount0Max: BigInt(1e36),
          amount1Max: BigInt(1e36),
        },
      ],
    })

    return await simulatedTransaction({
      config,
      simulateArgs: {
        abi: thickV2NfpMulticallAbi as Abi,
        account,
        address: nfpManagerAddress,
        args: [[decreaseData, collectData]],
        chainId,
        functionName: "multicall",
      },
    })
  } catch (error: unknown) {
    console.error("decreaseThickNfpPosition", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      code: error instanceof Error && "code" in error ? Number((error as { code: string }).code) : 0,
    }
  }
}

async function quoteLiveDecreaseMins({
  account,
  chainId,
  config,
  tokenId,
  liquidity,
  slippagePercent,
  deadline,
  nfpManagerAddress,
}: {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  liquidity: bigint
  slippagePercent: number
  deadline: number
  nfpManagerAddress: Address
}): Promise<[bigint, bigint]> {
  if (liquidity <= 0n) return [0n, 0n]
  try {
    const simulatedDecrease = await simulateContract(config, {
      abi: thickV2NfpPositionDecreaseLiquidityAbi,
      account,
      chainId,
      functionName: "decreaseLiquidity",
      address: nfpManagerAddress,
      args: [
        {
          tokenId,
          liquidity,
          amount0Min: 0n,
          amount1Min: 0n,
          deadline: BigInt(deadline),
        },
      ],
    })
    const result = simulatedDecrease?.result as readonly [bigint, bigint] | undefined
    if (!result) {
      throw new Error("Live decrease quote did not return withdrawal amounts.")
    }
    return [applySlippage(result[0], slippagePercent), applySlippage(result[1], slippagePercent)]
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown quote error"
    throw new Error(`Unable to quote live withdrawal amounts. Please refresh and try again. ${message}`)
  }
}

function applySlippage(amount: bigint, slippagePercent: number): bigint {
  if (amount === 0n) return 0n
  const bps = Math.max(0, Math.min(10_000, Math.floor((100 - slippagePercent) * 100)))
  return (amount * BigInt(bps)) / 10_000n
}
